import type { Sessao } from './auth';
import { CABECALHO_CSRF } from './auth';
import type { Resultado } from './acesso';
import { buscar } from './buscar';

/**
 * BLOCO 9 — o front de /api/central (telas 13 e 14). Todo número vem com a FONTE; o que depende de
 * fonte ausente vem indisponível com o motivo, e a tela mostra traço + motivo — nunca zero.
 */

export type Indicador<T> =
  | { readonly disponivel: true; readonly valor: T; readonly fonte: string }
  | { readonly disponivel: false; readonly porque: string };

export type Fontes = {
  readonly inovapay: {
    readonly disponivel: boolean;
    readonly ultima: string | null;
    readonly lancamentos: number;
  };
  readonly relatorioHos: {
    readonly disponivel: boolean;
    readonly desde: string | null;
    readonly ate: string | null;
    readonly cupons: number;
    readonly importadoEm: string | null;
  };
  readonly devolucoes: {
    readonly disponivel: boolean;
    readonly importadoEm: string | null;
    readonly periodo: { de: string; ate: string } | null;
    readonly fonte: 'hos_api' | 'pdf' | null;
  };
  readonly apiHos: {
    readonly integrada: boolean;
    readonly desde: string | null;
    readonly ultimaVenda: string | null;
  };
};

export type PontoDaEvolucao = {
  readonly dia: string;
  readonly faturamento: number;
  readonly cupons: number;
  readonly itens: number;
  readonly ticket: number | null;
  /** Margem do dia (%) — só com custo em ≥ 50% do faturamento do dia. */
  readonly margem: number | null;
  readonly referencia: {
    readonly faturamento: number | null;
    readonly cupons: number | null;
    readonly itens: number | null;
    readonly ticket: number | null;
  };
};

export type VisaoGeral = {
  readonly periodo: { readonly de: string; readonly ate: string };
  readonly fonte: 'relatorio_hos' | 'inovapay' | null;
  readonly fontes: Fontes;
  readonly faturamento: Indicador<string>;
  readonly cupons: Indicador<number>;
  readonly itens: Indicador<number>;
  readonly itensPorCupom: Indicador<string>;
  readonly ticketMedio: Indicador<string>;
  readonly margem: Indicador<string> & { readonly cobertura?: number };
  readonly comparacao: {
    readonly rotulo: string;
    readonly faturamento: number | null;
    readonly ticket: number | null;
  } | null;
  readonly evolucao: readonly PontoDaEvolucao[];
};

export type LinhaDaMovimentacao = {
  readonly id: string;
  readonly saleId: string | null;
  readonly tipo: 'venda' | 'devolucao' | 'cancelamento';
  readonly em: string | null;
  readonly dia: string;
  readonly cupom: string;
  readonly balconista: string | null;
  readonly balconistaConfirmado: boolean;
  readonly resumo: string;
  readonly itens: number;
  readonly valor: string;
  readonly status: 'concluida' | 'cancelada' | 'motivo_nao_informado' | 'devolvida';
};
export type Movimentacao = {
  readonly linhas: readonly LinhaDaMovimentacao[];
  readonly cupons: number;
  readonly itens: number;
  readonly fonteDisponivel: boolean;
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
  readonly resumo: {
    readonly cupons: number;
    readonly faturamento: string;
    readonly itens: number;
    /** Cancelamentos no caixa (API do HOS): cupons inteiros, itens dentro do cupom e o valor dos cupons. */
    readonly canceladas: {
      readonly cupons: number;
      readonly itens: number;
      readonly valor: string;
    };
    readonly devolucoes: { readonly cupons: number; readonly valor: string };
  };
  readonly balconistas: readonly {
    readonly codigo: string;
    readonly nome: string;
    readonly cupons: number;
  }[];
};

export type DetalheDoCupom = {
  readonly id: string;
  readonly cupom: string;
  readonly em: string;
  readonly balconista: string | null;
  readonly cancelada: boolean;
  readonly total: string;
  readonly desconto: string | null;
  /** Desconto do cupom ponderado pelo dinheiro (Σ desconto R$ ÷ Σ preço original). */
  readonly descontoPonderado: number | null;
  readonly descontoRs: string | null;
  readonly itens: readonly {
    readonly productId: string | null;
    /** Preço de tabela do HOS, derivado: praticado ÷ (1 − desconto%). */
    readonly precoOriginal: string | null;
    readonly descontoRs: string | null;
    readonly produto: string;
    readonly codigo: string | null;
    readonly ean: string | null;
    readonly quantidade: number;
    readonly base: string | null;
    readonly praticado: string | null;
    readonly pmc: string | null;
    readonly custo: string | null;
    readonly fonteDoCusto: 'venda' | 'cadastro' | null;
    readonly descontoPct: number | null;
    readonly margem: number | null;
    readonly valor: string | null;
  }[];
  readonly custoRestrito: boolean;
  readonly margem: number | null;
  readonly lancamentos: readonly {
    readonly produto: string;
    readonly quantidade: number;
    readonly premio: string;
    readonly balconista: string;
  }[];
  readonly linhaDoTempo: readonly { readonly em: string; readonly texto: string }[];
};

export type TipoDeExcecao =
  | 'abaixo_do_custo'
  | 'margem_negativa'
  | 'desconto_fora_do_padrao'
  | 'preco_abaixo_da_media'
  | 'sem_correspondencia'
  | 'queda_de_volume';
export type Excecao = {
  readonly tipo: TipoDeExcecao;
  readonly referencia: string;
  readonly dia: string;
  readonly em: string | null;
  readonly produto: string;
  readonly productId: string | null;
  readonly codigo: string | null;
  readonly balconista: string | null;
  readonly valores: Readonly<Record<string, string | number | null>>;
  readonly status: 'nova' | 'em_analise' | 'justificada';
  readonly observacao: string | null;
  readonly congelada: boolean;
};
export type Excecoes = {
  readonly excecoes: readonly Excecao[];
  readonly contagem: Readonly<Record<TipoDeExcecao, number>>;
  readonly desligadas: readonly string[];
  readonly desligadasPorque: string;
  readonly limiar: null;
  readonly limiarPorque: string;
};

export type PrecoDoProduto = {
  readonly productId: string;
  readonly produto: string;
  readonly codigo: string | null;
  readonly ean: string | null;
  readonly grupo: string | null;
  readonly base: string | null;
  readonly pmc: string | null;
  readonly custo: string | null;
  readonly fonteDoCusto: 'venda' | 'cadastro' | null;
  readonly medio30: string;
  readonly menor: string;
  readonly ultimo: string;
  readonly vendas: number;
  readonly quantidade: number;
  readonly faturamento: string;
  readonly margem: number | null;
  readonly lucro: string | null;
  readonly descontoMedio: number | null;
  readonly abaixoDoCusto: number;
  readonly desviosAbaixoDaMedia: number;
};
export type SituacaoDoPreco = 'todos' | 'negativa' | 'baixa' | 'abaixo_do_custo' | 'sem_custo';
export type OrdemDosPrecos = 'faturamento' | 'margem' | 'lucro' | 'quantidade' | 'desconto';
export type Precos = {
  readonly produtos: readonly PrecoDoProduto[];
  readonly total: number;
  readonly periodo: { readonly de: string; readonly ate: string };
  readonly semCustoNaFonte: number;
  readonly margem: Indicador<string>;
  readonly resumo: {
    readonly produtos: number;
    readonly faturamento: string;
    readonly faturamentoComCusto: string;
    readonly lucro: string | null;
    readonly negativas: number;
    readonly baixas: number;
    readonly vendasAbaixoDoCusto: number;
    readonly semCusto: number;
  };
  readonly porGrupo: readonly { grupo: string; faturamento: number; margem: number | null }[];
  readonly distribuicao: readonly { faixa: string; faturamento: number; produtos: number }[];
  readonly fonteDisponivel: boolean;
};

export type GrupoDoPeriodo = {
  readonly chave: string;
  readonly nome: string;
  readonly quantidade: number;
  readonly valor: number;
  readonly eventos: number;
};
export type Devolucoes = {
  readonly devolucoes: readonly {
    readonly cupom: string;
    readonly dia: string;
    readonly cod: string;
    readonly produto: string;
    readonly quantidade: number;
    readonly valor: string;
    readonly balconista: string;
    readonly recebidaPor: string | null;
    readonly motivo: string | null;
    readonly descontoNaPremiacao: string | null;
  }[];
  readonly cancelamentos: readonly {
    readonly id: string;
    readonly saleId: string | null;
    readonly tipo: 'item' | 'cupom';
    readonly cupom: string;
    readonly em: string;
    readonly balconista: string | null;
    readonly produto: string | null;
    readonly quantidade: number | null;
    readonly valor: string | null;
    readonly motivo: string | null;
  }[];
  readonly resumo: {
    readonly devolucoes: number;
    readonly cuponsDevolvidos: number;
    readonly valorDevolvido: string;
    readonly taxa: Indicador<string>;
    readonly semMotivo: number;
    readonly descontadoDaPremiacao: string;
    readonly itensCancelados: number;
    readonly cuponsCancelados: number;
    readonly valorCuponsCancelados: string;
  };
  readonly devolucoesPorDia: readonly { dia: string; valor: number; eventos: number }[];
  readonly cancelamentosPorDia: readonly { dia: string; itens: number; cupons: number }[];
  readonly devolucoesPorBalconista: readonly GrupoDoPeriodo[];
  readonly cancelamentosPorBalconista: readonly GrupoDoPeriodo[];
  readonly balconistas: readonly { readonly codigo: string; readonly nome: string }[];
};

async function ler<T>(caminho: string): Promise<Resultado<T>> {
  let r: Response;
  try {
    r = await buscar(caminho, {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok) return { ok: true, dados: (await r.json()) as T };
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

const q = (o: Record<string, string | null | undefined>) =>
  new URLSearchParams(Object.entries(o).filter((e): e is [string, string] => !!e[1])).toString();

export const lerVisao = (de: string, ate: string) =>
  ler<VisaoGeral>(`/api/central/visao?${q({ de, ate })}`);
export const lerMovimentacao = (
  de: string,
  ate: string,
  filtro: {
    tipo?: string | null;
    busca?: string | null;
    vendedor?: string | null;
    pagina?: number | null;
  } = {},
) =>
  ler<Movimentacao>(
    `/api/central/movimentacao?${q({
      de,
      ate,
      tipo: filtro.tipo,
      busca: filtro.busca,
      vendedor: filtro.vendedor,
      pagina: filtro.pagina && filtro.pagina > 1 ? String(filtro.pagina) : null,
    })}`,
  );
export const lerCupom = (id: string) => ler<DetalheDoCupom>(`/api/central/cupons/${id}`);
export const lerExcecoes = (de: string, ate: string) =>
  ler<Excecoes>(`/api/central/excecoes?${q({ de, ate })}`);
export const lerPrecos = (f: {
  busca?: string | null;
  de?: string;
  ate?: string;
  situacao?: SituacaoDoPreco | null;
  ordem?: OrdemDosPrecos | null;
}) => ler<Precos>(`/api/central/precos?${q({ ...f })}`);
/** Formas de pagamento do caixa no período (Vendas › Pagamentos). Dinheiro em CENTAVOS. */
export type PagamentosDoPeriodo = {
  readonly periodo: { readonly de: string; readonly ate: string };
  readonly totalCentavos: number;
  readonly somaCentavos: number;
  /** total declarado pelo HOS − soma das formas (0 = fecha). */
  readonly diferencaCentavos: number;
  readonly cupons: number;
  readonly formas: readonly {
    readonly forma: string;
    readonly rotulo: string;
    readonly cupons: number;
    readonly valorCentavos: number;
    readonly pct: number | null;
    readonly ticketCentavos: number | null;
  }[];
  readonly credito: {
    readonly valorCentavos: number;
    readonly aVistaCentavos: number;
    readonly parceladoCentavos: number;
    readonly pctParcelado: number | null;
    readonly parcelaMedia: number | null;
    readonly linhas: readonly {
      readonly parcelas: number;
      readonly cupons: number;
      readonly valorCentavos: number;
      readonly pctDoCredito: number | null;
    }[];
  };
  readonly serie: readonly {
    readonly dia: string;
    readonly totalCentavos: number;
    readonly porForma: Readonly<Record<string, number>>;
  }[];
  readonly diasSemDado: readonly string[];
  readonly diasComDado: number;
  readonly atualizadoEm: string | null;
};
export const lerPagamentos = (de: string, ate: string) =>
  ler<PagamentosDoPeriodo>(`/api/central/pagamentos?${q({ de, ate })}`);
export const lerDevolucoes = (de: string, ate: string, vendedor?: string | null) =>
  ler<Devolucoes>(`/api/central/devolucoes?${q({ de, ate, vendedor })}`);

export async function tratarExcecao(
  sessao: Sessao,
  e: {
    tipo: TipoDeExcecao;
    referencia: string;
    de: string;
    ate: string;
    status: 'em_analise' | 'justificada';
    observacao: string | null;
  },
): Promise<{ ok: true } | { ok: false; mensagem: string }> {
  let r: Response;
  try {
    r = await fetch('/api/central/excecoes', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [CABECALHO_CSRF]: sessao.csrfToken },
      body: JSON.stringify(e),
    });
  } catch {
    return { ok: false, mensagem: 'Sem conexão. Confira antes de repetir.' };
  }
  if (r.ok) return { ok: true };
  let m = '';
  try {
    m = ((await r.json()) as { error?: { message?: string } }).error?.message ?? '';
  } catch {
    // sem corpo
  }
  return { ok: false, mensagem: m || 'Não foi aceito.' };
}

/* ------------------------------------------------------------------ *
 * Período e formatos
 * ------------------------------------------------------------------ */

export type Janela = 'hoje' | 'ontem' | '7' | '30';
export function hojeBrasilia(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
}
export const menosDias = (dia: string, n: number) =>
  new Date(Date.parse(`${dia}T12:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);
export function periodoDaJanela(j: Janela): { de: string; ate: string } {
  const h = hojeBrasilia();
  if (j === 'hoje') return { de: h, ate: h };
  if (j === 'ontem') return { de: menosDias(h, 1), ate: menosDias(h, 1) };
  return { de: menosDias(h, Number(j) - 1), ate: h };
}
export const ROTULO_DA_EXCECAO: Record<TipoDeExcecao, string> = {
  abaixo_do_custo: 'Venda abaixo do custo',
  margem_negativa: 'Margem negativa',
  desconto_fora_do_padrao: 'Desconto fora do padrão',
  preco_abaixo_da_media: 'Preço abaixo da média',
  sem_correspondencia: 'Sem correspondência no HOS',
  queda_de_volume: 'Vendas abaixo da média',
};

/* ---------------------------------------------------------------- GIRO MAX (dono, 27/09) */

export interface ProdutoDoGiroMax {
  readonly productId: string;
  readonly codigo: string | null;
  readonly nome: string;
  readonly fabricante: string | null;
  readonly diasAcima: number;
  readonly unidadesNoPeriodo: number;
  readonly maiorDia: { readonly dia: string; readonly qtd: number };
}
export interface CupomDoGiroMax {
  readonly dia: string;
  readonly cupom: string;
  readonly saleId: string;
  readonly vendedor: string | null;
  readonly qtd: number;
}
export interface LinhaDoGiroMax extends CupomDoGiroMax {
  readonly productId: string;
  readonly produto: string;
  readonly codigo: string | null;
}
export interface GiroMax {
  readonly periodo: { readonly de: string; readonly ate: string };
  readonly acimaDe: number;
  readonly resumo: {
    readonly produtos: number;
    readonly ocorrencias: number;
    readonly unidades: number;
    readonly diaComMaisProdutos: { readonly dia: string; readonly produtos: number } | null;
  };
  readonly produtos: readonly ProdutoDoGiroMax[];
  readonly linhas: readonly LinhaDoGiroMax[];
  readonly linhasCortadas: boolean;
  readonly vendedores: readonly { readonly codigo: string; readonly nome: string }[];
  readonly fabricantes: readonly string[];
  readonly porTurnoEGrupo: readonly GrupoPorTurno[];
}
export type TurnoDoGiroMax = 'manha' | 'tarde';
export interface GrupoPorTurno {
  readonly grupo: string;
  readonly skus: number;
  readonly manha: { readonly skus: number; readonly unidades: number };
  readonly tarde: { readonly skus: number; readonly unidades: number };
  readonly madrugada: { readonly skus: number; readonly unidades: number };
}
export interface DiaDoGiroMax {
  readonly dia: string;
  readonly qtd: number;
  readonly cupons: number;
}
export interface ProdutoDoGiroMaxDetalhe {
  readonly produto: {
    readonly productId: string;
    readonly codigo: string | null;
    readonly nome: string;
    readonly fabricante: string | null;
  };
  readonly acimaDe: number;
  readonly unidades: number;
  readonly diasComVenda: number;
  readonly diasAcima: number;
  readonly maiorDia: DiaDoGiroMax | null;
  readonly menorDia: DiaDoGiroMax | null;
  readonly dias: readonly DiaDoGiroMax[];
  readonly vendedores: readonly {
    readonly nome: string;
    readonly qtd: number;
    readonly cupons: number;
  }[];
  readonly cupons: readonly CupomDoGiroMax[];
}
export interface FiltroDoGiroMax {
  readonly de: string;
  readonly ate: string;
  readonly acimaDe: number;
  readonly busca?: string | null;
  readonly fabricante?: string | null;
  readonly vendedor?: string | null;
  readonly comTaxas?: boolean;
  readonly turno?: TurnoDoGiroMax | null;
}

export const lerGiroMax = (f: FiltroDoGiroMax) =>
  ler<GiroMax>(
    `/api/central/giro-max?${q({
      de: f.de,
      ate: f.ate,
      acimaDe: String(f.acimaDe),
      busca: f.busca,
      fabricante: f.fabricante,
      vendedor: f.vendedor,
      comTaxas: f.comTaxas ? '1' : null,
      turno: f.turno,
    })}`,
  );
export const lerGiroMaxDoProduto = (
  productId: string,
  f: Pick<FiltroDoGiroMax, 'de' | 'ate' | 'acimaDe' | 'vendedor' | 'turno'>,
) =>
  ler<ProdutoDoGiroMaxDetalhe>(
    `/api/central/giro-max/produtos/${encodeURIComponent(productId)}?${q({
      de: f.de,
      ate: f.ate,
      acimaDe: String(f.acimaDe),
      vendedor: f.vendedor,
      turno: f.turno,
    })}`,
  );

/* ---------------------------------------------------------------- RANKING DA LOJA (dono, 27/09) */

export interface LinhaDoRankingDaLoja {
  readonly posicao: number;
  readonly codigo: string;
  readonly nome: string;
  /** false = vende no HOS sem conta no InovaPay (não disputa o "melhor vendedor"). */
  readonly temConta: boolean;
  readonly faturamento: string;
  readonly vendas: string;
  readonly devolucoes: string;
  readonly cupons: number;
  readonly itens: number;
  readonly ticketMedio: string | null;
  readonly itensPorCupom: number | null;
}
export const lerRankingDaLoja = (de: string, ate: string) =>
  ler<{ de: string; ate: string; linhas: readonly LinhaDoRankingDaLoja[] }>(
    `/api/central/ranking-loja?${q({ de, ate })}`,
  );

/* ---------------------------------------------------------------- CENTRAL DE DESCONTOS (fase 1) */

export type OperadorDaRegra = 'gt' | 'gte' | 'lt' | 'entre';
export interface RegraDeDesconto {
  readonly grupo: string;
  readonly op: OperadorDaRegra;
  readonly a: number;
  readonly b?: number | null;
}
export type SituacaoDoItemVendido =
  | 'abaixo_do_custo'
  | 'margem_negativa'
  | 'margem_abaixo'
  | 'sem_custo'
  | 'com_desconto'
  | 'sem_desconto';
export type OrdemDosProdutosComDesconto =
  | 'desconto_rs'
  | 'desconto_pct'
  | 'faturamento'
  | 'quantidade'
  | 'lucro'
  | 'margem'
  | 'maior_desconto'
  | 'abaixo_do_custo';
export interface FiltroDeDescontos {
  readonly grupos: readonly string[];
  readonly vendedor: string | null;
  readonly busca: string | null;
  readonly fabricante: string | null;
  readonly dmin: number | null;
  readonly dmax: number | null;
  readonly regras: readonly RegraDeDesconto[];
  readonly situacao: SituacaoDoItemVendido | null;
  readonly margemAbaixo: number | null;
  readonly hde: number | null;
  readonly hate: number | null;
  readonly dias: readonly number[];
}
export interface NumerosDeDesconto {
  readonly itens: number;
  readonly cupons: number;
  readonly quantidade: number;
  readonly vendido: string;
  readonly original: string;
  readonly descontoRs: string;
  readonly descontoPct: number | null;
  readonly semDescontoConhecido: number;
  readonly vendidoComCusto: string | null;
  readonly custo: string | null;
  readonly lucro: string | null;
  readonly margem: number | null;
  readonly lucroPorReal: number | null;
  readonly abaixoDoCusto: number | null;
  readonly perdaAbaixoDoCusto: string | null;
  readonly semCusto: number | null;
}
export type ResumoDeDescontos = NumerosDeDesconto & {
  readonly produtos: number;
  readonly vendedores: number;
};
export interface VendedorDeDescontos extends NumerosDeDesconto {
  readonly chave: string;
  readonly nome: string;
  readonly ticketMedio: string | null;
}
export interface ProdutoDeDescontos extends NumerosDeDesconto {
  readonly productId: string;
  readonly produto: string;
  readonly codigo: string | null;
  readonly grupo: string;
  readonly fabricante: string | null;
  readonly maiorDescontoPct: number | null;
}
export interface CentralDeDescontos {
  readonly periodo: { readonly de: string; readonly ate: string };
  readonly resumo: ResumoDeDescontos;
  readonly resumoDaLoja: ResumoDeDescontos | null;
  readonly porGrupo: readonly (NumerosDeDesconto & { readonly grupo: string })[];
  readonly porVendedor: readonly VendedorDeDescontos[];
  readonly porFaixa: readonly (NumerosDeDesconto & {
    readonly faixa: string;
    readonly de: number;
    readonly ate: number | null;
    readonly produtos: number;
  })[];
  readonly produtos: {
    readonly linhas: readonly ProdutoDeDescontos[];
    readonly total: number;
    readonly pagina: number;
    readonly porPagina: number;
  };
  readonly opcoes: {
    readonly grupos: readonly string[];
    readonly fabricantes: readonly string[];
    readonly vendedores: readonly { readonly chave: string; readonly nome: string }[];
  };
  readonly custoDesde: string | null;
}
export interface ItemComDesconto {
  readonly itemId: string;
  readonly saleId: string;
  readonly cupom: string;
  readonly em: string;
  readonly productId: string;
  readonly produto: string;
  readonly grupo: string;
  readonly vendedor: string | null;
  readonly quantidade: number;
  readonly precoOriginal: string | null;
  readonly precoVendido: string | null;
  readonly vendido: string;
  readonly descontoPct: number | null;
  readonly descontoRs: string | null;
  readonly custo: string | null;
  readonly lucro: string | null;
  readonly margem: number | null;
  readonly abaixoDoCusto: boolean | null;
}

const consultaDeDescontos = (de: string, ate: string, f: FiltroDeDescontos) => ({
  de,
  ate,
  grupos: f.grupos.length ? f.grupos.join('|') : null,
  vendedor: f.vendedor,
  busca: f.busca,
  fabricante: f.fabricante,
  dmin: f.dmin === null ? null : String(f.dmin),
  dmax: f.dmax === null ? null : String(f.dmax),
  regras: f.regras.length ? JSON.stringify(f.regras) : null,
  situacao: f.situacao,
  margemAbaixo: f.margemAbaixo === null ? null : String(f.margemAbaixo),
  hde: f.hde === null ? null : String(f.hde),
  hate: f.hate === null ? null : String(f.hate),
  dias: f.dias.length ? f.dias.join(',') : null,
});

export const lerCentralDeDescontos = (
  de: string,
  ate: string,
  f: FiltroDeDescontos,
  o: { ordem?: OrdemDosProdutosComDesconto | null; pagina?: number },
) =>
  ler<CentralDeDescontos>(
    `/api/central/descontos?${q({
      ...consultaDeDescontos(de, ate, f),
      ordem: o.ordem ?? null,
      pagina: o.pagina && o.pagina > 1 ? String(o.pagina) : null,
    })}`,
  );
export const lerItensComDesconto = (
  de: string,
  ate: string,
  f: FiltroDeDescontos,
  o: {
    productId?: string | null;
    ordem?: 'recentes' | 'desconto_pct' | 'desconto_rs' | 'prejuizo';
    pagina?: number;
  },
) =>
  ler<{
    linhas: readonly ItemComDesconto[];
    total: number;
    pagina: number;
    porPagina: number;
  }>(
    `/api/central/descontos/itens?${q({
      ...consultaDeDescontos(de, ate, f),
      productId: o.productId ?? null,
      ordem: o.ordem ?? null,
      pagina: o.pagina && o.pagina > 1 ? String(o.pagina) : null,
    })}`,
  );
